import type {
  FleetBill,
  FleetBrowserTakeover,
  FleetDailyActivity,
  FleetLoopOutlookStatus,
  FleetQueueName,
  FleetRun,
  FleetRunJudge,
  FleetRunState,
  FleetStep,
  FleetStepGroup,
  FleetSummary,
  FleetVerdict
} from './claude-fleet.types'

import { slugify } from '@/composables/useDataTestid'
import { formatDurationMs } from '@/utils/format-duration'

export const FLEET_ARTIFACTS_BUCKET = 'tattle-claude-artifacts'
export const FLEET_ARTIFACTS_REGION = 'us-east-1'
export const FLEET_LOOP_PREFIX = 'tattle-claude-runner-'
export const FLEET_MESSAGE_MAX_LENGTH = 20000
export const FLEET_LIVE_POLL_MS = 5000

const DAY_NAMES: Record<string, string> = {
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  '1': 'Sunday',
  '2': 'Monday',
  '3': 'Tuesday',
  '4': 'Wednesday',
  '5': 'Thursday',
  '6': 'Friday',
  '7': 'Saturday'
}

/** A run that still has a live agent behind it, so the drawer can talk to it. */
export function isRunLive(state: FleetRunState | undefined): boolean {
  return state === 'running' || state === 'paused'
}

/**
 * The S3 console page for the run's artifacts prefix. `artifacts` may arrive as a bare key prefix or
 * as an `s3://tattle-claude-artifacts/...` URI. A runner run's objects are files beside its name
 * (`runner/runs/<id>.jsonl`, `.out`, `.session.tgz`), so that prefix gets no trailing slash; any
 * other prefix is a folder.
 */
export function buildTranscriptUrl(artifacts: string | undefined): string | null {
  if (!artifacts) return null
  const key = artifacts.replace(/^s3:\/\/[^/]+\//, '').replace(/^\/+/, '')
  const prefix = RUNNER_RUN_PREFIX.test(key) ? key : key.replace(/\/?$/, '/')
  if (prefix === '/') return null
  const query = new URLSearchParams({
    region: FLEET_ARTIFACTS_REGION,
    prefix,
    showversions: 'false'
  })
  return `https://${FLEET_ARTIFACTS_REGION}.console.aws.amazon.com/s3/buckets/${FLEET_ARTIFACTS_BUCKET}?${query}`
}

const RUNNER_RUN_PREFIX = /^runner\/runs\/[^/]+$/

/** What a run that starts no Claude session (a bookkeeping run) shows where its steps would be. */
export const NO_SESSION_STEPS_TEXT = 'This run starts no Claude session, so it has no steps'

/** What the record of such a run says in place of its transcript link. */
export const NO_SESSION_RECORD_TEXT = 'This run starts no Claude session, so it has no steps or transcript.'

/** How often an open drawer re-reads a live run's steps; the fleet uploads its transcript about every 15 seconds. */
export const FLEET_LIVE_STEPS_POLL_MS = 8000

/** The activity whose runs the Runs list hides unless asked: it runs every few minutes and starts no Claude session. */
export const BOOKKEEPING_ACTIVITY = 'bookkeeping'

/**
 * A run that starts no Claude session, so it has no steps or transcript to read. The fleet marks these
 * `sessionless`; a fleet service older than that field is read by activity instead.
 */
export function runStartsNoSession(run: Partial<Pick<FleetRun, 'sessionless' | 'activity'>> | undefined): boolean {
  if (!run) return false
  return run.sessionless === true || run.activity === BOOKKEEPING_ACTIVITY
}

/**
 * The live steps' clock: how long the run has been going and how old the transcript read is, e.g.
 * `Live · 3m 12s · updated 9s ago`. Either part is left out when its time is unknown.
 */
export function liveClockText({ startedAt, updatedAt }: { startedAt?: string; updatedAt?: string }, now: number): string {
  const parts = ['Live']
  const elapsed = elapsedSince(startedAt, now)
  if (elapsed !== null) parts.push(formatDurationMs(elapsed))
  const age = elapsedSince(updatedAt, now)
  if (age !== null) parts.push(`updated ${formatDurationMs(age)} ago`)
  return parts.join(' · ')
}

function elapsedSince(iso: string | undefined, now: number): number | null {
  const at = iso ? Date.parse(iso) : NaN
  return Number.isFinite(at) ? Math.max(0, now - at) : null
}

/** A running run's subagents so far, `3 agents`; empty when the fleet did not say or there are none. */
export function runAgentsText(run: Pick<FleetRun, 'agents'>): string {
  const agents = run.agents
  if (typeof agents !== 'number' || agents <= 0) return ''
  return agents === 1 ? '1 agent' : `${agents} agents`
}

/** The activity a loop runs, read from its schedule name (`tattle-claude-runner-<activity>`). */
export function activityFromLoopName(name: string): string {
  return name.startsWith(FLEET_LOOP_PREFIX) ? name.slice(FLEET_LOOP_PREFIX.length) : name
}

export function runDurationMs(run: Pick<FleetRun, 'durationMs' | 'startedAt' | 'finishedAt'>): number | null {
  if (typeof run.durationMs === 'number') return run.durationMs
  if (!run.finishedAt) return null
  const elapsed = Date.parse(run.finishedAt) - Date.parse(run.startedAt)
  return Number.isFinite(elapsed) && elapsed >= 0 ? elapsed : null
}

/** A verdict's pill colour: held green, reversed and failed red, a second ask or a stall yellow. */
export function verdictColor(verdict: FleetVerdict | undefined): 'green' | 'red' | 'yellow' | 'gray' {
  switch (verdict) {
    case 'held':
      return 'green'
    case 'reversed':
    case 'failed':
      return 'red'
    case 'follow-up':
    case 'stalled':
      return 'yellow'
    default:
      return 'gray'
  }
}

/** A grade's pill colour: pass green, concern yellow, fail red. */
export function judgeColor(verdict: FleetRunJudge['verdict'] | undefined): 'green' | 'yellow' | 'red' | 'gray' {
  if (verdict === 'pass') return 'green'
  if (verdict === 'concern') return 'yellow'
  if (verdict === 'fail') return 'red'
  return 'gray'
}

export function runStateColor(state: FleetRunState): 'blue' | 'green' | 'red' | 'yellow' | 'gray' {
  switch (state) {
    case 'running':
      return 'blue'
    case 'ok':
      return 'green'
    case 'error':
      return 'red'
    case 'paused':
      return 'yellow'
    default:
      return 'gray'
  }
}

function pad(value: string): string {
  return value.padStart(2, '0')
}

function describeRate(body: string): string {
  const [amount, unit] = body.trim().split(/\s+/)
  if (amount === '1') return `Every ${unit.replace(/s$/, '')}`
  return `Every ${amount} ${unit}`
}

function describeDays(dayOfWeek: string, dayOfMonth: string): string | null {
  if (dayOfWeek === '?' || dayOfWeek === '*') {
    return dayOfMonth === '*' || dayOfMonth === '?' ? 'Daily' : null
  }
  if (dayOfWeek === 'MON-FRI' || dayOfWeek === '2-6') return 'Weekdays'
  const range = /^([A-Z]{3})-([A-Z]{3})$/.exec(dayOfWeek)
  if (range) return DAY_NAMES[range[1]] && DAY_NAMES[range[2]] ? `${DAY_NAMES[range[1]]} to ${DAY_NAMES[range[2]]}` : null
  const names = dayOfWeek.split(',').map(day => DAY_NAMES[day])
  return names.every(Boolean) ? names.join(', ') : null
}

/**
 * An hour or minute field's step form: `*\/2`, `0/2`, `8/2` (from 8), `8-18/2` and `8-18` (a range, step 1).
 * `from` and `to` are the first and last values it fires on, or null when it is not one of those forms.
 */
function stepField(field: string, max: number): { step: number; from: number; to: number } | null {
  const match = /^(\*|\d+)(?:-(\d+))?(?:\/(\d+))?$/.exec(field)
  if (!match || (!match[2] && !match[3])) return null
  const from = match[1] === '*' ? 0 : Number(match[1])
  const end = match[2] !== undefined ? Number(match[2]) : max
  const step = match[3] !== undefined ? Number(match[3]) : 1
  if (step < 1 || from > end || end > max) return null
  return { step, from, to: from + Math.floor((end - from) / step) * step }
}

/** The step of a minute field that fires through the whole hour (`*\/5`, `0/5`), or null for any other form. */
function wholeHourMinuteStep(minute: string): number | null {
  const step = stepField(minute, 59)
  return step && step.from === 0 && step.to + step.step > 59 ? step.step : null
}

/** "Weekdays every" before a window; "Daily" adds nothing to a schedule that repeats within the day. */
function windowLead(days: string): string {
  return days === 'Daily' ? 'Every' : `${days} every`
}

/** Every N minutes within a range of hours: "Weekdays every 10 minutes, 09:00–17:59". */
function describeMinuteWindow(everyMinutes: number, hour: string, days: string): string | null {
  const hourStep = stepField(hour, 23)
  if (!hourStep || hourStep.step !== 1) return null
  return `${windowLead(days)} ${everyMinutes} minutes, ${pad(String(hourStep.from))}:00–${pad(String(hourStep.to))}:59`
}

/** A schedule that fires once an hour at `minute` (two digits) on the hours `hour` names. */
function describeHours(hour: string, minute: string, days: string): string | null {
  const lead = days === 'Daily' ? 'Every' : `${days}, every`
  const hourStep = stepField(hour, 23)
  const wholeDay = hourStep !== null && hourStep.from === 0 && hourStep.to + hourStep.step > 23
  if (hour === '*' || (wholeDay && hourStep?.step === 1)) return `${lead} hour at :${minute}`
  if (hourStep && wholeDay) return `${lead} ${hourStep.step} hours at :${minute}`
  if (hourStep) {
    // A window within the day: "Weekdays every 2 hours, 08:00–18:00".
    const window = `${pad(String(hourStep.from))}:${minute}–${pad(String(hourStep.to))}:${minute}`
    return `${windowLead(days)} ${hourStep.step === 1 ? 'hour' : `${hourStep.step} hours`}, ${window}`
  }
  if (!/^\d+(,\d+)*$/.test(hour)) return null
  return `${days} at ${hour.split(',').map(h => `${pad(h)}:${minute}`).join(', ')}`
}

function describeCron(body: string): string | null {
  const fields = body.trim().split(/\s+/)
  if (fields.length < 5) return null
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields
  if (month !== '*') return null

  // A step that fires only in part of each hour, such as 5-30/5, is left as written.
  const everyMinutes = wholeHourMinuteStep(minute)
  if (everyMinutes && hour === '*') return `Every ${everyMinutes} minutes`
  if (minute === '*' && hour === '*') return 'Every minute'

  const days = describeDays(dayOfWeek, dayOfMonth)
  if (!days) return null
  if (everyMinutes) return describeMinuteWindow(everyMinutes, hour, days)
  if (!/^\d+$/.test(minute)) return null
  return describeHours(hour, pad(minute), days)
}

/**
 * An EventBridge Scheduler expression in words — `cron(0 10 ? * MON-FRI *)` reads "Weekdays at
 * 10:00". An expression this does not recognise is returned as written rather than guessed at.
 */
export function describeSchedule(expression: string, timezone?: string): string {
  const match = /^(rate|cron|at)\((.*)\)$/.exec(expression.trim())
  let words: string | null = null
  if (match?.[1] === 'rate') words = describeRate(match[2])
  if (match?.[1] === 'cron') words = describeCron(match[2])
  if (match?.[1] === 'at') words = `Once at ${match[2]}`
  const described = words ?? expression
  return timezone && words && match?.[1] === 'cron' ? `${described} (${timezone})` : described
}

/** Why an operator-only action is disabled for this person, or nothing when they may act. `offReason`
 *  replaces the operator wording when the page does not know whether this person is an operator. */
export function operatorOnlyReason(canAct: boolean, action: string, offReason?: string | null): string | undefined {
  if (canAct) return undefined
  return offReason ?? `Only fleet operators can ${action}.`
}

/** A YYYY-MM-DD day as "Oct 9". */
export function formatShortDay(day: string): string {
  const date = new Date(`${day}T12:00:00Z`)
  return Number.isNaN(date.getTime()) ? day : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** An ISO time as "Oct 20, 10:06 AM" in the viewer's time zone, or nothing when it is missing or unreadable. */
export function formatDateTime(iso: string | undefined): string {
  const when = iso ? new Date(iso) : null
  if (!when || Number.isNaN(when.getTime())) return ''
  return when.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** An ISO time as a 24-hour clock time, HH:mm:ss, or nothing when it is missing or unreadable. */
export function formatClockTime(iso: string | undefined): string {
  if (!iso) return ''
  const time = new Date(iso)
  return Number.isNaN(time.getTime()) ? '' : time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
}

/** Why the two cost figures differ, shown beside them. */
export const FLEET_COST_HELP =
  'Model cost adds up what each run reported for its model calls, at list price, from the run records, ' +
  'and what Talk it over spent answering questions, priced the same way; the monthly budget counts both. ' +
  'The AWS bill is what Cost Explorer has charged for the fleet so far: Claude on Bedrock, plus the ' +
  'AgentCore Runtime instances runs execute on, AgentCore Browser sessions and everything else (Lambda, ' +
  'SQS, builds). It lags about a day, and its model line also counts calls no run record covers. ' +
  'The projection straight-lines the bill to the end of the month.'

export const FLEET_QUEUE_LABELS: Record<FleetQueueName, string> = {
  mention: 'Mention queue',
  'run-start': 'Run-start queue'
}

/** The bill's parts in the order the page lists them, leaving out a part that cost nothing. */
export function billParts(bill: FleetBill): { key: string; label: string; usd: number }[] {
  return [
    { key: 'model', label: 'Model (Bedrock)', usd: bill.modelUsd },
    { key: 'runtime', label: 'Runtime (AgentCore)', usd: bill.runtimeUsd },
    { key: 'browser', label: 'Browser (AgentCore)', usd: bill.browserUsd },
    { key: 'other', label: 'Other', usd: bill.otherUsd }
  ].filter(part => part.usd > 0)
}

export interface FleetModelSpendView {
  spentUsd: number
  budgetUsd: number
  /** Spent against the budget, 0–100. */
  percent: number
  /** The model cost straight-lined to the month's end; null on the 1st, when one day says too little. */
  projectedUsd: number | null
  /** Where the projection lands on the budget bar, 0–100. */
  projectedPercent: number | null
  leftUsd: number
  /** Whole days after today until the month ends. */
  daysLeft: number
}

/** The New York calendar day of `now`, and how many days its month has. */
function newYorkMonthDay(now: Date): { day: number; daysInMonth: number } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric' })
      .formatToParts(now)
      .map(part => [part.type, Number(part.value)])
  )
  return { day: parts.day!, daysInMonth: new Date(Date.UTC(parts.year!, parts.month!, 0)).getUTCDate() }
}

/** The Claude model cost against the monthly budget, as the Spend card shows it. The month is New York's. */
export function modelSpendView(summary: Pick<FleetSummary, 'monthModelUsd' | 'budgetUsd'>, now: Date): FleetModelSpendView {
  const { day, daysInMonth } = newYorkMonthDay(now)
  const spentUsd = summary.monthModelUsd
  const budgetUsd = summary.budgetUsd
  const ofBudget = (usd: number) => (budgetUsd > 0 ? Math.min(100, Math.round((usd / budgetUsd) * 100)) : 0)
  const projectedUsd = day > 1 ? (spentUsd / day) * daysInMonth : null
  return {
    spentUsd,
    budgetUsd,
    percent: ofBudget(spentUsd),
    projectedUsd,
    projectedPercent: projectedUsd === null ? null : ofBudget(projectedUsd),
    leftUsd: Math.max(0, budgetUsd - spentUsd),
    daysLeft: daysInMonth - day
  }
}

/** How full a figure is against the month's budget, as the progress bar's colour. */
export function budgetColor(percent: number): 'red600' | 'orange600' | 'blue600' {
  if (percent >= 90) return 'red600'
  if (percent >= 75) return 'orange600'
  return 'blue600'
}

/** An activity is quiet when the fleet's 14 days hold no run of it; unknown without the series. */
export function isActivityQuiet(days: FleetDailyActivity[] | undefined): boolean {
  return Boolean(days?.length) && days!.every(day => day.runs === 0)
}

export function outlookColor(status: FleetLoopOutlookStatus): 'green' | 'yellow' | 'red' | 'gray' {
  switch (status) {
    case 'runs':
      return 'green'
    case 'limited':
      return 'yellow'
    case 'skipped':
      return 'red'
    default:
      return 'gray'
  }
}

export const OUTLOOK_LABELS: Record<FleetLoopOutlookStatus, string> = {
  runs: 'Will run',
  limited: 'Limited',
  skipped: 'Skipped',
  off: 'Off'
}

/** A step group's heading: the subagent and what it was asked, or the run's own session. */
export function stepGroupTitle(group: FleetStepGroup): string {
  if (group.id === 'main') return 'Main session'
  const agent = group.agent ?? 'Subagent'
  return group.description ? `${agent} — ${group.description}` : agent
}

/** The run's newest step across its session and subagents, by time; a step with no time is never newest. */
export function latestStep(groups: FleetStepGroup[]): { groupId: string; index: number; step: FleetStep } | null {
  let latest: { groupId: string; index: number; step: FleetStep } | null = null
  for (const group of groups) {
    for (const [index, step] of group.steps.entries()) {
      if (step.at && (!latest || step.at >= (latest.step.at ?? ''))) latest = { groupId: group.id, index, step }
    }
  }
  return latest
}

/** A step's element id in a steps list, so a link can scroll to it. */
export function stepAnchor(sectionTag: string, groupId: string, index: number): string {
  return `${sectionTag}-step-${slugify(groupId)}-${index}`
}

/** A dollar figure small enough for one step: tenths of a cent shown, under a tenth shown as such. */
export function formatStepCost(usd: number | undefined): string {
  if (typeof usd !== 'number') return ''
  if (usd > 0 && usd < 0.001) return '<$0.001'
  return `$${usd.toFixed(usd < 1 ? 3 : 2)}`
}

/** "Taken over by pat@… since 14:05:12", or nothing while the agent drives. */
export function takenOverText(takeover: FleetBrowserTakeover | null | undefined): string {
  if (!takeover?.who) return ''
  const since = formatClockTime(takeover.at)
  return since ? `Taken over by ${takeover.who} since ${since}` : `Taken over by ${takeover.who}`
}

/** "12 min", "3 h", "2 d": how long something has waited, for a tile or a heading. */
export function waitedText(ageMs: number): string {
  const minutes = Math.floor(ageMs / 60_000)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return hours < 48 ? `${hours} h` : `${Math.floor(hours / 24)} d`
}
